import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { TrendingUp, AlertCircle, Loader2, CheckCircle, Users } from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'

export const Register = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isHydrated = useAuthStore(state => state.isHydrated)
  const isAuthenticated = useAuthStore(state => state.checkAuth())
  
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '')
  const [referralValid, setReferralValid] = useState<boolean | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isValidatingReferral, setIsValidatingReferral] = useState(false)

  useEffect(() => {
    if (isHydrated && isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isHydrated, isAuthenticated, navigate])

  useEffect(() => {
    const validateReferral = async () => {
      if (referralCode.length === 8 && /^[A-Z0-9]{8}$/.test(referralCode)) {
        setIsValidatingReferral(true)
        try {
          const response = await fetch(`/api/v1/auth/referral/validate/${referralCode.toUpperCase()}`)
          setReferralValid(response.ok)
        } catch {
          setReferralValid(false)
        } finally {
          setIsValidatingReferral(false)
        }
      } else {
        setReferralValid(null)
      }
    }
    
    const debounce = setTimeout(validateReferral, 500)
    return () => clearTimeout(debounce)
  }, [referralCode])

  const validateForm = () => {
    if (!firstName || !lastName || !email || !password || !confirmPassword || !referralCode) {
      setError('Tutti i campi sono obbligatori')
      return false
    }

    if (firstName.trim().length < 2) {
      setError('Il nome deve essere almeno 2 caratteri')
      return false
    }

    if (lastName.trim().length < 2) {
      setError('Il cognome deve essere almeno 2 caratteri')
      return false
    }

    if (!/^[A-Z0-9]{8}$/.test(referralCode)) {
      setError('Il codice referral deve essere di 8 caratteri alfanumerici')
      return false
    }

    if (referralValid === false) {
      setError('Codice referral non valido')
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Inserisci un indirizzo email valido')
      return false
    }

    if (password.length < 8) {
      setError('La password deve contenere almeno 8 caratteri')
      return false
    }

    if (password !== confirmPassword) {
      setError('Le password non corrispondono')
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess(false)

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      const response = await fetch('/api/v1/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          referral_code: referralCode.toUpperCase()
        })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || 'Registrazione fallita')
      }

      setSuccess(true)
      
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante la registrazione')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-10 w-10 text-primary" />
              <span className="text-2xl font-bold">TradeBridgePro</span>
            </div>
          </div>
          <div className="text-center">
            <CardTitle className="text-2xl">Crea Account</CardTitle>
            <CardDescription>Registrati per iniziare a utilizzare la piattaforma</CardDescription>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-destructive/10 text-destructive rounded-md">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {success && (
              <div className="flex items-center space-x-2 p-3 bg-green-500/10 text-green-600 rounded-md">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Registrazione completata! Reindirizzamento al login...</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="referralCode">Codice Invito</Label>
              <div className="relative">
                <Users className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="referralCode"
                  type="text"
                  placeholder="ABCDEFGH"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                  required
                  autoFocus
                  disabled={success}
                  className="pl-10 uppercase tracking-widest font-mono"
                  maxLength={8}
                />
                {isValidatingReferral && (
                  <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />
                )}
                {!isValidatingReferral && referralValid === true && (
                  <CheckCircle className="absolute right-3 top-3 h-4 w-4 text-green-500" />
                )}
                {!isValidatingReferral && referralValid === false && (
                  <AlertCircle className="absolute right-3 top-3 h-4 w-4 text-red-500" />
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Inserisci il codice di 8 caratteri ricevuto da chi ti ha invitato
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Nome</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Mario"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  disabled={success}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Cognome</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Rossi"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  disabled={success}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tua.email@esempio.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={success}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Minimo 8 caratteri"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={success}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Conferma Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Ripeti la password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={success}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={isLoading || success}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registrazione in corso...
                </>
              ) : success ? (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Registrato con successo!
                </>
              ) : (
                'Registrati'
              )}
            </Button>

            <div className="text-center">
              <Link 
                to="/login" 
                className="text-sm text-primary hover:underline"
              >
                Hai già un account? Accedi
              </Link>
            </div>

            <div className="text-center text-sm text-muted-foreground">
              <p>Creando un account accetti i nostri termini di servizio</p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
